import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { ArrowRight, CheckCircle2, Clock, Compass } from "lucide-react";

// Phase d'avancement dérivée de l'état mission serveur (source de vérité)
const MISSION_PHASE = {
  AWAITING_PICKUP: 0, LOADED: 1,
  DELIVERY_PENDING_CONFIRMATION: 2, DELIVERED: 2, SETTLED: 2,
  DISPUTED: 2, DISPUTE_ESCALATED: 2, DISPUTE_RESOLVED: 2,
};
const missionPhase = (req) => MISSION_PHASE[req.mission_state] ?? 0;

function MissionCard({ req }) {
  const acceptedOffer = req.offers?.find(o => o.status === "accepted");
  const amount = acceptedOffer?.price_eur || 0;
  const fee    = Math.round(amount * 0.10);
  const proNet = amount - fee;
  const phase  = missionPhase(req);
  const pct    = phase === 0 ? 33 : phase === 1 ? 66 : 100;

  const statusCfg = {
    accepted:    { label: "ACCEPTÉE",  cls: "text-blue-600 bg-blue-50 border-blue-200",   dot: "bg-blue-500" },
    in_progress: { label: "EN COURS", cls: "text-orange-600 bg-orange-50 border-orange-200", dot: "bg-orange-500 animate-pulse" },
  }[req.status] || { label: req.status, cls: "text-slate-500 bg-slate-100 border-slate-200", dot: "bg-slate-400" };

  const phaseLabel = ["Prise en charge", "En transit", "Livré"][phase] || "Prise en charge";

  return (
    <Link to={`/requests/${req.request_id}`} className="block">
      <div className="bg-white border border-slate-200 hover:border-orange-300 rounded-xl p-5 transition-all hover:shadow-sm group">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusCfg.dot}`} />
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${statusCfg.cls}`}>
                {statusCfg.label}
              </span>
              <span className="text-[11px] text-slate-400 font-mono">#{req.request_id?.slice(-6).toUpperCase()}</span>
            </div>
            <div className="font-display font-bold text-slate-900 text-lg flex items-center gap-2 group-hover:text-orange-600 transition-colors">
              {req.pickup?.city}
              <ArrowRight className="w-4 h-4 text-orange-500 flex-shrink-0" />
              {req.dropoff?.city}
            </div>
            <div className="text-sm text-slate-500 mt-0.5">
              {Math.round(req.distance_km || 0)} km · {req.vehicle_type}
              {req.client_name ? ` · ${req.client_name}` : ""}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="font-display font-extrabold text-2xl text-slate-900">{proNet} €</div>
            <div className="text-xs text-slate-400">net après commission</div>
          </div>
        </div>

        {/* Barre progression (en cours) */}
        {req.status === "in_progress" && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-500">{phaseLabel}</span>
              <span className="text-xs font-bold text-orange-500">{pct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-500 rounded-full transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              {["Prise en charge", "En transit", "Livré"].map((s, i) => (
                <span key={s} className={`text-[10px] ${i <= phase ? "text-orange-500 font-semibold" : "text-slate-300"}`}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Alerte action requise (acceptée) */}
        {req.status === "accepted" && (
          <div className="flex items-center gap-2 text-xs text-blue-600 font-semibold bg-blue-50 rounded-lg px-3 py-2">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            En attente du paiement client — confirmez la prise en charge une fois payé
          </div>
        )}

        {/* Infos pickup */}
        {req.pickup_date && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
            Date prévue : {new Date(req.pickup_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Missions() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get("/pro/missions");
        if (!cancelled) setMissions(data);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const accepted    = missions.filter(r => r.status === "accepted");
  const in_progress = missions.filter(r => r.status === "in_progress");

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-2xl">

          <div className="mb-6">
            <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight">Mes missions</h1>
            <p className="text-sm text-slate-500 mt-1">
              {loading ? "Chargement…" : `${missions.length} mission${missions.length !== 1 ? "s" : ""} active${missions.length !== 1 ? "s" : ""}`}
            </p>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-400">Chargement des missions…</div>
          ) : missions.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <div className="font-display font-bold text-xl text-slate-900">Aucune mission en cours</div>
              <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
                Les missions que vous acceptez apparaîtront ici, de la prise en charge jusqu'à la livraison.
              </p>
              <Link to="/dashboard">
                <button className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-display font-bold transition-colors">
                  <Compass className="w-4 h-4" /> Voir le marché ouvert
                </button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {in_progress.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                    <h2 className="font-display font-bold text-base text-slate-900">En cours ({in_progress.length})</h2>
                  </div>
                  <div className="space-y-3">
                    {in_progress.map(r => <MissionCard key={r.request_id} req={r} />)}
                  </div>
                </section>
              )}

              {accepted.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <h2 className="font-display font-bold text-base text-slate-900">Acceptées — en attente de paiement ({accepted.length})</h2>
                  </div>
                  <div className="space-y-3">
                    {accepted.map(r => <MissionCard key={r.request_id} req={r} />)}
                  </div>
                </section>
              )}
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
